/**
 * Connect WhatsApp (QM-A13-003, customer self-connect): Meta embedded signup -> /v1/whatsapp-connect.
 *
 * Flow: on open, POST /embedded-signup/start (single-use state, 10 min) and load the Facebook JS SDK. The button
 * calls FB.login synchronously, so the browser treats the popup as user-initiated. Meta returns the OAuth code in
 * the login callback and the WABA id + phone number id by postMessage (WA_EMBEDDED_SIGNUP); with both, the page
 * POSTs /embedded-signup/complete, which verifies everything with Meta server side and seals the token.
 *
 * Phones (and the "Continue in this tab" link after a cancel or a blocked popup) use the same-tab redirect flow
 * instead (signup-redirect.ts): the whole tab goes to Meta's OAuth dialog and comes back to
 * /whatsapp/connect/callback (WhatsAppConnectCallbackPage).
 *
 * This page is reached by a full document load only (plain <a href>, never client routing): the portal worker
 * relaxes the CSP / opener policy for exactly this path (worker/security.ts WHATSAPP_CONNECT_PATH).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Heading, Panel, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { BffError, bff, bffJson } from '../../../lib/bff';
import { OAUTH_ERROR_CODE } from '@qm/contracts/meta-oauth-error';
import { buildSignupDialogUrl, isMobileBrowser } from './signup-redirect';
import { BrandLogo } from '../../../shell/BrandLogo';
import { InAppBrowserNotice } from '../../../shell/InAppBrowserNotice';
import { connectGate, currentInAppBrowser } from '../../../lib/in-app-browser';

export type SignupStart = { state: string; appId: string | null; configId: string | null; graphApiVersion: string; expiresInSeconds: number; redirectUri?: string | null };
/** phoneNumberId is absent when Meta's WhatsApp Business app onboarding session reports only the WABA */
export type SignupSession = { wabaId: string; phoneNumberId: string | null; coexistence: boolean };
export type WaNumber = { id: string; displayPhoneNumber: string | null; verifiedName: string | null; status: string; hasToken?: boolean; tokenSource?: 'sealed' | 'system' | null };

/**
 * Badge for a connected number. An active number without a stored token (e.g. mapped by Quixo Support) cannot send:
 * it shows "Reconnect needed", never "Active".
 */
export function numberBadge(n: Pick<WaNumber, 'status' | 'hasToken'>): { key: string; tone: 'neutral' | 'warning' | 'error' } {
  if (n.status === 'active') return n.hasToken === false ? { key: 'whatsapp-connect.numbers.status.reconnect', tone: 'warning' } : { key: 'whatsapp-connect.numbers.status.active', tone: 'neutral' };
  return { key: `whatsapp-connect.numbers.status.${n.status === 'released' ? 'released' : 'disconnected'}`, tone: 'error' };
}

/**
 * Lets a business keep its number on the WhatsApp Business app ("Coexistence"): Meta's flow offers to connect the
 * existing app or to start with a new number, so new-number signups are unchanged.
 */
export const WA_FEATURE_TYPE = 'whatsapp_business_app_onboarding';

/** Origins Meta posts the embedded signup session from. Anything else is ignored. */
export const META_MESSAGE_ORIGINS = ['https://www.facebook.com', 'https://web.facebook.com'] as const;
const META_ID = /^\d{5,25}$/;
/** refresh the single-use state a minute before the API forgets it */
const STATE_REFRESH_MS = 9 * 60_000;
/** how long to wait for Meta's session message after the login callback returned a code */
const SESSION_WAIT_MS = 15_000;

/** errorCode: Meta's numeric error_id from the ERROR event, when it sent one (the free-text message is not kept) */
export type SignupMessage = { kind: 'finish'; session: SignupSession } | { kind: 'cancel' } | { kind: 'error'; errorCode?: string } | null;

/** Parse Meta's WA_EMBEDDED_SIGNUP postMessage. Wrong origin, wrong type or malformed ids -> null (ignored). */
export function parseSignupMessage(origin: string, raw: unknown): SignupMessage {
  if (!(META_MESSAGE_ORIGINS as readonly string[]).includes(origin)) return null;
  let msg: unknown = raw;
  if (typeof raw === 'string') { try { msg = JSON.parse(raw); } catch { return null; } }
  if (!msg || typeof msg !== 'object') return null;
  const m = msg as { type?: unknown; event?: unknown; data?: { waba_id?: unknown; phone_number_id?: unknown; error_id?: unknown } };
  if (m.type !== 'WA_EMBEDDED_SIGNUP') return null;
  if (m.event === 'CANCEL') return { kind: 'cancel' };
  if (m.event === 'ERROR') {
    const id = String(m.data?.error_id ?? '');
    return OAUTH_ERROR_CODE.test(id) ? { kind: 'error', errorCode: id } : { kind: 'error' };
  }
  if (m.event === 'FINISH' || m.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
    const coexistence = m.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING';
    const wabaId = String(m.data?.waba_id ?? '');
    const pn = m.data?.phone_number_id;
    const phoneNumberId = pn === undefined || pn === null || pn === '' ? null : String(pn);
    // a regular FINISH must name the number; the business-app session may name only the WABA
    if (!META_ID.test(wabaId) || (phoneNumberId === null ? !coexistence : !META_ID.test(phoneNumberId))) return { kind: 'error' };
    return { kind: 'finish', session: { wabaId, phoneNumberId, coexistence } };
  }
  return null;
}

/** API error -> translation key for this page. */
export function signupErrorKey(e: unknown): string {
  if (e instanceof BffError) {
    const msg = e.envelope?.error.message ?? '';
    if (e.status === 409) return 'whatsapp-connect.claim.pending';
    if (e.envelope?.error.code === 'validation_failed' && /permission/i.test(msg)) return 'whatsapp-connect.signup.permissionsMismatch';
    if (/expired/i.test(msg)) return 'whatsapp-connect.signup.expired';
    if (/does not belong/i.test(msg)) return 'whatsapp-connect.signup.notInWaba';
    if (/No WhatsApp number/i.test(msg)) return 'whatsapp-connect.signup.noNumbers';
  }
  return 'whatsapp-connect.signup.failed';
}

type FbLoginResponse = { authResponse?: { code?: string } | null };
type FbSdk = { init(o: Record<string, unknown>): void; login(cb: (r: FbLoginResponse) => void, o: Record<string, unknown>): void };
type FbWindow = Window & { FB?: FbSdk; fbAsyncInit?: () => void };

/** Load the SDK once. The bundle that inserts it carries the CSP nonce, so 'strict-dynamic' trusts it. */
function loadSdk(appId: string, version: string): Promise<FbSdk> {
  const w = window as FbWindow;
  if (w.FB) return Promise.resolve(w.FB);
  return new Promise((resolve, reject) => {
    w.fbAsyncInit = () => { w.FB!.init({ appId, autoLogAppEvents: false, xfbml: false, version }); resolve(w.FB!); };
    const s = document.createElement('script');
    s.src = 'https://connect.facebook.net/en_US/sdk.js';
    s.async = true;
    s.crossOrigin = 'anonymous';
    const nonce = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content');
    if (nonce) s.nonce = nonce;
    s.onerror = () => reject(new Error('sdk_load_failed'));
    document.head.appendChild(s);
  });
}

type Phase = 'loading' | 'ready' | 'not_configured' | 'working' | 'done';

export function WhatsAppConnectPage() {
  const { t } = useApp();
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState<string | null>(null);
  const [numbers, setNumbers] = useState<WaNumber[] | null>(null);
  const [dialogUrl, setDialogUrl] = useState<string | null>(null);
  // decided once on the client (phones: same-tab dialog only); without a navigator the desktop layout is shown
  const [mobile] = useState(() => typeof navigator !== 'undefined' && isMobileBrowser(navigator as Navigator & { userAgentData?: { mobile?: boolean } }));
  // Meta blocks its signup in in-app browsers (Outlook, Facebook, ...): suggest Safari/Chrome before the first try
  const [gate] = useState(() => connectGate(currentInAppBrowser()));
  const [held, setHeld] = useState(false);
  const start = useRef<SignupStart | null>(null);
  const sdk = useRef<FbSdk | null>(null);
  const session = useRef<SignupSession | null>(null);
  const waiting = useRef<((s: SignupSession | null) => void) | null>(null);

  const loadNumbers = useCallback(() => { void bff<WaNumber[]>('/v1/whatsapp-connect/numbers').then(setNumbers, () => setNumbers([])); }, []);

  const prepare = useCallback(async () => {
    try {
      const s = await bffJson<SignupStart>('/v1/whatsapp-connect/embedded-signup/start', 'POST');
      if (!s.appId || !s.configId) { setPhase('not_configured'); return; }
      start.current = s;
      setDialogUrl(buildSignupDialogUrl(s));
      // phones use the same-tab dialog only; the SDK popup is not loaded there
      if (!mobile) sdk.current = await loadSdk(s.appId, s.graphApiVersion);
      setPhase((p) => (p === 'loading' ? 'ready' : p));
    } catch (e) {
      setError(signupErrorKey(e));
      setPhase('ready');
    }
  }, [mobile]);

  useEffect(() => {
    loadNumbers();
    void prepare();
    const timer = window.setInterval(() => { if (!session.current) void prepare(); }, STATE_REFRESH_MS);
    const onMessage = (ev: MessageEvent) => {
      const m = parseSignupMessage(ev.origin, ev.data);
      if (!m) return;
      if (m.kind === 'finish') { session.current = m.session; waiting.current?.(m.session); }
      else if (m.kind === 'cancel') { waiting.current?.(null); setError('whatsapp-connect.signup.cancelled'); }
      else {
        waiting.current?.(null);
        setError('whatsapp-connect.signup.metaError');
        // audit Meta's error id against this attempt; that consumes the state, so fetch a fresh one for the retry
        const st = start.current;
        if (st) {
          start.current = null;
          void bffJson('/v1/whatsapp-connect/embedded-signup/provider-error', 'POST', { state: st.state, error: 'embedded_signup_error', ...(m.errorCode ? { errorCode: m.errorCode } : {}) })
            .catch(() => undefined).finally(() => { setPhase('loading'); void prepare(); });
        }
      }
    };
    // back from Meta's same-tab dialog via the back button (page restored from the back/forward cache)
    const onPageShow = (ev: PageTransitionEvent) => { if (ev.persisted) setPhase((p) => (p === 'working' ? 'ready' : p)); };
    window.addEventListener('message', onMessage);
    window.addEventListener('pageshow', onPageShow);
    return () => { window.clearInterval(timer); window.removeEventListener('message', onMessage); window.removeEventListener('pageshow', onPageShow); };
  }, [loadNumbers, prepare]);

  const sessionInfo = () => (session.current ? Promise.resolve(session.current) : new Promise<SignupSession | null>((resolve) => {
    const tm = window.setTimeout(() => resolve(null), SESSION_WAIT_MS);
    waiting.current = (s) => { window.clearTimeout(tm); waiting.current = null; resolve(s); };
  }));

  const finish = async (code: string) => {
    const s = await sessionInfo();
    const st = start.current;
    if (!s || !st) { setError('whatsapp-connect.signup.failed'); setPhase('ready'); return; }
    try {
      const n = await bffJson<WaNumber>('/v1/whatsapp-connect/embedded-signup/complete', 'POST', {
        state: st.state, code, wabaId: s.wabaId,
        ...(s.phoneNumberId ? { phoneNumberId: s.phoneNumberId } : {}),
        ...(s.coexistence ? { coexistence: true } : {}),
      });
      setConnected(n.displayPhoneNumber ?? n.verifiedName ?? s.phoneNumberId ?? s.wabaId);
      setPhase('done');
      loadNumbers();
    } catch (e) {
      setError(signupErrorKey(e));
      session.current = null; start.current = null;
      setPhase('loading');
      void prepare(); // the state was consumed; get a fresh one for the next try
    }
  };

  // Must stay synchronous up to FB.login so the browser allows the popup.
  const connect = () => {
    if (gate.shouldHold()) { setHeld(true); return; }
    if (mobile) { if (dialogUrl) { setPhase('working'); window.location.assign(dialogUrl); } return; }
    const fb = sdk.current; const st = start.current;
    if (!fb || !st?.configId) return;
    setError(null); session.current = null; setPhase('working');
    fb.login((r) => {
      const code = r?.authResponse?.code;
      // a Meta ERROR event may already have explained the failure; otherwise it was a cancel
      if (!code) { setError((e) => e ?? 'whatsapp-connect.signup.cancelled'); setPhase((p) => (p === 'working' ? 'ready' : p)); return; }
      void finish(code);
    }, { config_id: st.configId, response_type: 'code', override_default_response_type: true, extras: { setup: {}, featureType: WA_FEATURE_TYPE, sessionInfoVersion: '3' } });
  };

  return (
    <section className="qm-page" aria-labelledby="page-title">
      <div className="qm-brand-title"><BrandLogo provider="whatsapp" size={32} /><Heading level={1} id="page-title">{t('whatsapp-connect.page.title')}</Heading></div>
      <Text tone="muted">{t('whatsapp-connect.page.intro')}</Text>
      <Panel aria-labelledby="wa-connect">
        <Heading level={2} id="wa-connect">{t('whatsapp-connect.signup.start')}</Heading>
        <Text tone="muted">{t('whatsapp-connect.signup.permissions')}</Text>
        {phase === 'not_configured' ? <Text role="alert">{t('whatsapp-connect.signup.notConfigured')}</Text> : (
          <Button onClick={connect} disabled={phase !== 'ready'}>
            {phase === 'loading' ? t('whatsapp-connect.signup.loading') : phase === 'working' ? t('whatsapp-connect.signup.connecting') : t('whatsapp-connect.signup.start')}
          </Button>
        )}
        {held ? (
          <InAppBrowserNotice
            copy={{
              title: t('whatsapp-connect.inApp.title'), body: t('whatsapp-connect.inApp.body'), copyLink: t('whatsapp-connect.inApp.copy'),
              copied: t('whatsapp-connect.inApp.copied'), copyManual: t('whatsapp-connect.inApp.manual'), continueAnyway: t('whatsapp-connect.inApp.continue'),
            }}
            // stays synchronous up to FB.login / the redirect, so the popup is still user-initiated
            onContinue={() => { gate.acknowledge(); setHeld(false); connect(); }}
          />
        ) : null}
        {error ? <Text role="alert">{t(error)}</Text> : null}
        {!mobile && dialogUrl && phase === 'ready' ? (
          <Text tone="muted">{t('whatsapp-connect.signup.sameTabHint')} <a href={dialogUrl} onClick={(e) => { if (gate.shouldHold()) { e.preventDefault(); setHeld(true); } }}>{t('whatsapp-connect.signup.sameTab')}</a></Text>
        ) : null}
        {phase === 'done' && connected ? <Text role="status">{t('whatsapp-connect.signup.success', { number: connected })}</Text> : null}
      </Panel>
      <Panel aria-labelledby="wa-numbers">
        <Heading level={2} id="wa-numbers">{t('whatsapp-connect.numbers.title')}</Heading>
        {numbers && numbers.length === 0 ? <Text tone="muted">{t('whatsapp-connect.numbers.empty')}</Text> : null}
        {numbers && numbers.length > 0 ? (
          <ul>
            {numbers.map((n) => {
              const badge = numberBadge(n);
              return (
                <li key={n.id}>
                  <bdi dir="ltr">{n.displayPhoneNumber ?? n.id}</bdi>{n.verifiedName ? ` · ${n.verifiedName}` : ''}{' '}
                  <Badge tone={badge.tone}>{t(badge.key)}</Badge>
                  {badge.tone === 'warning' ? <Text tone="muted">{t('whatsapp-connect.numbers.reconnectHint')}</Text> : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </Panel>
    </section>
  );
}
